import type { ReactNode } from 'react';
import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import { Tooltip } from '@radix-ui/themes';
import { useTranslation, type MessageKey } from '../../i18n';
import type { ToolpadId } from '../../types';

export interface SidebarRailItem {
  id: ToolpadId;
  icon: ReactNode;
  titleKey: MessageKey;
}

interface SidebarColumnProps {
  side: 'left' | 'right';
  isOpen: boolean;
  onCollapse: () => void;
  onExpandToolpad: (id: ToolpadId) => void;
  items: SidebarRailItem[];
  children: ReactNode;
}

export function SidebarColumn({
  side,
  isOpen,
  onCollapse,
  onExpandToolpad,
  items,
  children,
}: SidebarColumnProps) {
  const { t } = useTranslation();
  const CollapseIcon = side === 'left' ? DoubleArrowLeftIcon : DoubleArrowRightIcon;

  return (
    <aside
      className={`editor-sidebar ${side}${isOpen ? '' : ' is-collapsed'}`}
      aria-expanded={isOpen}
    >
      {isOpen ? (
        <>
          <div className="editor-sidebar-header">
            <div className="editor-sidebar-header-spacer" />
            <Tooltip content={t('sidebar.collapse')}>
              <button
                type="button"
                className="editor-sidebar-header-action"
                onClick={onCollapse}
                aria-label={t('sidebar.collapse')}
              >
                <CollapseIcon width={14} height={14} />
              </button>
            </Tooltip>
          </div>
          <div className="editor-sidebar-body">{children}</div>
        </>
      ) : (
        <div className="editor-sidebar-rail" role="toolbar" aria-label={t('sidebar.expand')}>
          {items.map((item) => (
            <Tooltip key={item.id} content={t(item.titleKey)} side={side === 'left' ? 'right' : 'left'}>
              <button
                type="button"
                className="editor-sidebar-rail-btn"
                onClick={() => onExpandToolpad(item.id)}
                aria-label={t(item.titleKey)}
              >
                {item.icon}
              </button>
            </Tooltip>
          ))}
        </div>
      )}
    </aside>
  );
}
